import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { PageHeader, BrutalCard } from "../components/brutal";
import { Send, User, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

function useQueryParam(name) {
  const { search } = useLocation();
  return new URLSearchParams(search).get(name);
}

export default function ReferralFormPage() {
  const initialJobId = useQueryParam("jobId") || "";
  const [jobs, setJobs] = useState([]);
  const [stages, setStages] = useState([]);
  const [form, setForm] = useState({
    candidate_name: "",
    candidate_email: "",
    candidate_phone: "",
    job_id: initialJobId,
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/jobs?status=open").then((r) => setJobs(r.data));
    api.get("/stages").then((r) => setStages(r.data)).catch(() => setStages([]));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.job_id) {
      toast.error("Please select a job");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const { data } = await api.post("/referrals", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Referral submitted! +${stages[0]?.points ?? 10} points awarded.`);
      nav("/app/my-referrals");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Refer a Candidate"
        subtitle={`Recommend someone you'd love to work with. You'll get points every step of the way — up to ${stages.filter((s) => !s.is_rejected).reduce((sum, s) => sum + (s.points || 0), 0) || 210} if they pass probation.`}
      />

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-5">
        <BrutalCard className="p-6 lg:col-span-2" data-testid="referral-form">
          <h2 className="font-display font-black text-2xl mb-5">Candidate details</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">
                <User className="w-3 h-3 inline mr-1" /> Full name
              </label>
              <input
                required
                className="input-brutal"
                value={form.candidate_name}
                onChange={(e) => update("candidate_name", e.target.value)}
                placeholder="Jane Doe"
                data-testid="referral-name"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">
                <Mail className="w-3 h-3 inline mr-1" /> Email
              </label>
              <input
                required
                type="email"
                className="input-brutal"
                value={form.candidate_email}
                onChange={(e) => update("candidate_email", e.target.value)}
                placeholder="jane@example.com"
                data-testid="referral-email"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">
                <Phone className="w-3 h-3 inline mr-1" /> Phone (optional)
              </label>
              <input
                className="input-brutal"
                value={form.candidate_phone}
                onChange={(e) => update("candidate_phone", e.target.value)}
                placeholder="+49 ..."
                data-testid="referral-phone"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Role to refer for</label>
              <select
                required
                className="input-brutal"
                value={form.job_id}
                onChange={(e) => update("job_id", e.target.value)}
                data-testid="referral-job"
              >
                <option value="">Select a role…</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>{j.title} — {j.location}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-xs font-bold uppercase tracking-widest mb-2">Why are they a good fit?</label>
            <textarea
              rows={4}
              className="input-brutal resize-none"
              value={form.note}
              onChange={(e) => update("note", e.target.value)}
              placeholder="Their strengths, past work, how you know them…"
              data-testid="referral-note"
            />
          </div>

          <button type="submit" disabled={busy} className="btn-brutal btn-primary w-full mt-6 py-3 text-base" data-testid="referral-submit">
            <Send className="w-4 h-4" /> {busy ? "Submitting…" : `Submit referral${stages.length ? ` (+${stages[0]?.points ?? 10} points)` : ""}`}
          </button>
        </BrutalCard>

        <BrutalCard className="p-6 bg-brand-yellow" color="bg-brand-yellow">
          <h3 className="font-display font-black text-xl">Points you can earn</h3>
          <ul className="mt-4 space-y-2.5 text-sm" data-testid="points-breakdown">
            {stages
              .filter((s) => !s.is_rejected)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((s) => (
                <li key={s.id} className="flex items-center justify-between border-b-2 border-black/20 pb-1.5">
                  <span className="font-medium">{s.name}</span>
                  <span className="font-display font-black">+{s.points}</span>
                </li>
              ))}
          </ul>
          <div className="mt-6 border-2 border-black rounded-xl p-3 bg-white">
            <div className="text-[11px] uppercase tracking-widest font-bold">Total possible</div>
            <div className="font-display font-black text-4xl" data-testid="total-possible">
              {stages.filter((s) => !s.is_rejected).reduce((sum, s) => sum + (s.points || 0), 0)} pts
            </div>
            <div className="text-xs text-neutral-600">
              = €{stages.filter((s) => !s.is_rejected).reduce((sum, s) => sum + (s.points || 0), 0)} on your payroll
            </div>
          </div>
        </BrutalCard>
      </form>
    </div>
  );
}
